// Single-page subscription configuration (create + edit): setup, pricing and policies in one view.
import React, {useEffect, useRef, useState} from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import {useSubscription} from 'context/SubscriptionContext';
import {useSubscriptionPricing} from 'context/SubscriptionPricingContext';
import {useSubscriptionConfig} from 'context/SubscriptionConfigContext';
import {useDuration} from 'context/DurationContext';
import {useCountry} from 'context/CountryContext';
import {useSubscriptionForm} from 'hooks/useSubscriptionForm';
import {subscriptionApi} from 'api/subscriptionApi';
import {ENTITY_CONFIG} from 'config/entityConfig';
import {isDiscountInputAllowed} from 'utils/subscriptionRulesUtils';
import SubscriptionSetupSection from './SubscriptionSetupSection';
import SubscriptionSummaryPanel from './SubscriptionSummaryPanel';
import CompactPricingSection from './CompactPricingSection';
import CompactPoliciesSection from './CompactPoliciesSection';

const SectionHeader = ({index, title, subtitle, mb = 2}) => (
  <Box display="flex" alignItems="center" gap={1.5} mb={mb}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: '#1976d2',
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
      {index}
    </Box>
    <Box>
      <Typography variant="subtitle1" sx={{fontWeight: 700, color: '#1e293b'}}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="textSecondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const POLICY_SECTIONS = ['coolingOffPeriod', 'renewal', 'cancellation', 'billing'];

// The API can return decimals as strings ("20.00"); the inputs and schema expect numbers.
const toConfigValue = value =>
  typeof value === 'string' && value.trim() !== '' && !isNaN(value)
    ? Number(value)
    : value;

const firstMessage = node => {
  if (!node || typeof node !== 'object') return null;
  if (typeof node.message === 'string' && node.message) return node.message;
  for (const child of Object.values(node)) {
    const found = firstMessage(child);
    if (found) return found;
  }
  return null;
};

const SubscriptionSinglePage = ({
  onComplete,
  onCancel,
  initialData = null,
  isEditMode = false,
}) => {
  const {
    subscriptionType,
    moduleType,
    duration,
    country,
    setStep,
    setSubscriptionType,
    setModuleType,
    setDuration,
    setCountry,
    resetSubscription,
  } = useSubscription();

  const {
    pricingStrategy,
    setPricingStrategyConfig,
    updateGlobalPricing,
    setEntityOverride,
    clearAllOverrides,
  } = useSubscriptionPricing();

  const {resetConfig, updateNestedConfig} = useSubscriptionConfig();
  const {loading: durationLoading} = useDuration();
  const {loading: countryLoading} = useCountry();

  const {trigger, getFormData, setValue, methods} = useSubscriptionForm();

  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [setupErrors, setSetupErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const setupRef = useRef(null);
  const pricingRef = useRef(null);

  const setupComplete = !!(
    subscriptionType &&
    moduleType &&
    duration &&
    country
  );
  const isDiscountAllowed = isDiscountInputAllowed(subscriptionType);

  // Everything is validated against the full (pricing + policy) schema on submit.
  useEffect(() => {
    setStep(3);
  }, [setStep]);

  // Create mode: start clean (contexts are app-wide, so stale values may exist).
  // Edit mode: preload the saved configuration.
  useEffect(() => {
    resetSubscription();
    if (!isEditMode) {
      resetConfig();
      clearAllOverrides();
      setPricingStrategyConfig({
        strategy: null,
        moduleType: null,
        subscriptionType: null,
        appliedToEntities: [],
      });
      setStep(3);
      return;
    }
    if (!initialData) return;

    resetConfig();

    setTimeout(() => {
      setStep(3);
      if (initialData.subscriptionType)
        setSubscriptionType(initialData.subscriptionType);
      if (initialData.moduleType)
        setModuleType(initialData.moduleType.toUpperCase());
      if (initialData.duration?.id) setDuration(initialData.duration.id);
      else if (typeof initialData.duration === 'string')
        setDuration(initialData.duration);
      if (initialData.country?.id) setCountry(initialData.country.id);
      else if (typeof initialData.country === 'string')
        setCountry(initialData.country);

      // Saved policies (cooling-off, renewal, cancellation, billing) - leaf by leaf so
      // any key missing from the saved record keeps its default.
      POLICY_SECTIONS.forEach(section => {
        Object.entries(initialData.config?.[section] || {}).forEach(
          ([key, value]) => {
            if (value === null || value === undefined) return;
            updateNestedConfig(`${section}.${key}`, toConfigValue(value));
          },
        );
      });

      if (initialData.pricing?.strategy) {
        setPricingStrategyConfig({
          strategy: initialData.pricing.strategy,
          moduleType: initialData.moduleType?.toUpperCase(),
          subscriptionType: initialData.subscriptionType,
        });

        if (initialData.pricing.strategy === 'global') {
          updateGlobalPricing(initialData.subscriptionType, {
            discountPercentage:
              parseFloat(initialData.pricing.globalDiscount) || 0,
            moduleType: initialData.moduleType?.toUpperCase(),
          });
        }

        if (
          initialData.pricing.strategy === 'custom' &&
          initialData.pricing.entityOverrides
        ) {
          initialData.pricing.entityOverrides.forEach(override => {
            setEntityOverride(override.entityId, initialData.subscriptionType, {
              discountPercentage: override.discountPercentage || 0,
              moduleType: initialData.moduleType?.toUpperCase(),
              entityName: override.entityName || override.entityId,
              entityType: override.entityType || initialData.moduleType,
            });
          });
        }
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, initialData]);

  const clearSetupError = field =>
    setSetupErrors(prev => (prev[field] ? {...prev, [field]: ''} : prev));

  // Entities and pricing rules depend on type/module, so a saved strategy no longer applies.
  const resetPricingChoice = () => {
    if (!pricingStrategy.strategy) return;
    clearAllOverrides();
    setPricingStrategyConfig({strategy: null, appliedToEntities: []});
    setValue('pricingStrategy.strategy', '');
  };

  const handleTypeChange = type => {
    if (type !== subscriptionType) resetPricingChoice();
    setSubscriptionType(type);
    setValue('subscriptionType', type);
    clearSetupError('subscriptionType');
  };

  const handleModuleChange = selected => {
    if (selected !== moduleType) resetPricingChoice();
    setModuleType(selected);
    setValue('moduleType', selected);
    clearSetupError('moduleType');
  };

  const handleDurationChange = id => {
    setDuration(id);
    setValue('duration', id);
    clearSetupError('duration');
  };

  const handleCountryChange = id => {
    setCountry(id);
    setValue('country', id);
    clearSetupError('country');
  };

  const handleStrategyChange = async strategy => {
    setPricingStrategyConfig({
      subscriptionType,
      moduleType,
      strategy,
      appliedToEntities: [],
    });
    setValue('pricingStrategy.strategy', strategy);
    setValue('pricingStrategy.moduleType', moduleType);
    setSubmitError('');
    await trigger('pricingStrategy');
  };

  const scrollTo = ref =>
    ref.current?.scrollIntoView({behavior: 'smooth', block: 'center'});

  const handleSubmit = async () => {
    setSubmitError('');

    const missing = {};
    if (!subscriptionType) missing.subscriptionType = 'Subscription type is required';
    if (!moduleType) missing.moduleType = 'Module is required';
    if (!duration) missing.duration = 'Duration is required';
    if (!country) missing.country = 'Country is required';
    if (Object.keys(missing).length) {
      setSetupErrors(missing);
      scrollTo(setupRef);
      return;
    }

    if (!pricingStrategy.strategy) {
      setSubmitError('Please select a pricing strategy');
      scrollTo(pricingRef);
      return;
    }

    const isValid = await trigger();
    if (!isValid) {
      setSubmitError(
        firstMessage(methods.formState.errors) ||
          'Please review the highlighted fields',
      );
      return;
    }

    try {
      setIsLoading(true);
      const payload = getFormData();
      if (isEditMode && initialData?.id) payload.id = initialData.id;

      const response = isEditMode
        ? await subscriptionApi.updateSubscription(
            payload.id || initialData.id,
            payload,
          )
        : await subscriptionApi.createSubscription(payload);

      setIsDone(true);
      onComplete?.({apiResponse: response, isEditMode});
    } catch (err) {
      console.error('Subscription submission error:', err);
      setSubmitError(
        err.message ||
          `Failed to ${isEditMode ? 'update' : 'create'} subscription. Please try again.`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (durationLoading || countryLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const sectionSx = {p: {xs: 2, sm: 3}, borderRadius: 2, borderColor: '#e2e8f0'};

  return (
    <Grid container spacing={3} alignItems="flex-start">
      <Grid item xs={12} md={8}>
        {isEditMode && (
          <Alert severity="info" sx={{mb: 2}}>
            <strong>Edit Mode:</strong> You are editing an existing subscription
            configuration.
          </Alert>
        )}

        {/* 1. SETUP */}
        <Paper variant="outlined" sx={{...sectionSx, mb: 2}} ref={setupRef}>
          <SectionHeader
            index={1}
            title="Setup"
            subtitle="Choose what this configuration is for"
          />
          <SubscriptionSetupSection
            isEditMode={isEditMode}
            errors={setupErrors}
            onTypeChange={handleTypeChange}
            onModuleChange={handleModuleChange}
            onDurationChange={handleDurationChange}
            onCountryChange={handleCountryChange}
          />
        </Paper>

        {/* 2. PRICING */}
        <Paper
          variant="outlined"
          sx={{...sectionSx, mb: 2, opacity: setupComplete ? 1 : 0.55}}
          ref={pricingRef}>
          <SectionHeader
            index={2}
            title="Pricing & Discount"
            subtitle={
              setupComplete
                ? `For ${ENTITY_CONFIG.SUBSCRIPTION?.[moduleType]?.label || moduleType}`
                : 'Complete the setup above to unlock'
            }
          />

          {setupComplete && (
            <CompactPricingSection
              subscriptionType={subscriptionType}
              moduleType={moduleType}
              duration={duration}
              country={country}
              pricingStrategy={pricingStrategy}
              onStrategyChange={handleStrategyChange}
              isDiscountAllowed={isDiscountAllowed}
            />
          )}
        </Paper>

        {/* 3. POLICIES */}
        <Paper
          variant="outlined"
          sx={{...sectionSx, opacity: setupComplete ? 1 : 0.55}}>
          <SectionHeader
            index={3}
            mb={1}
            title="Policies"
            subtitle={
              setupComplete
                ? 'Defaults are pre-filled - change only what you need'
                : 'Complete the setup above to unlock'
            }
          />

          {setupComplete && (
            <CompactPoliciesSection subscriptionType={subscriptionType} />
          )}
        </Paper>

        {submitError && (
          <Alert severity="error" sx={{mt: 2}}>
            {submitError}
          </Alert>
        )}
      </Grid>

      {/* SUMMARY + ACTION */}
      <Grid item xs={12} md={4}>
        <SubscriptionSummaryPanel
          isEditMode={isEditMode}
          isLoading={isLoading}
          disabled={isDone}
          onSubmit={handleSubmit}
          onBack={onCancel}
        />
      </Grid>
    </Grid>
  );
};

export default SubscriptionSinglePage;
